import { EpicIcons, FormDialogButton } from "@constellation-academy/epic-ui";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { editSubscriberConfigButton_EditSubscriberConfigMutation } from "@relay/editSubscriberConfigButton_EditSubscriberConfigMutation.graphql";
import {
	EditSubscriberConfigForm,
	EditSubscriberConfigFormState,
} from "@screens/offer-subscriptions/parts/edit-subscriber-config-form";
import { EDIT_SUBSCRIBER_CONFIG_MUTATION } from "./edit-subscriber-config-button.graphql";
import { EditSubscriberConfigButtonProps } from "./edit-subscriber-config-button.types";

export const EditSubscriberConfigButton = ({
	offerId,
	currentTitle,
	currentImageUrl,
	currentImageFileId,
	currentVisibility,
	currentDateTime,
	currentLicenseProduct,
	currentCourseContentProduct,
	currentCertificationProduct,
	onUpdated,
}: EditSubscriberConfigButtonProps) => {
	const { t } = useTranslation("common");
	const toast = useRef<Toast>(null);

	const [editSubscriberConfig, isEditing] =
		useMutation<editSubscriberConfigButton_EditSubscriberConfigMutation>(
			EDIT_SUBSCRIBER_CONFIG_MUTATION,
		);

	const handleSubmit = (onHide: () => void, values: EditSubscriberConfigFormState) => {
		editSubscriberConfig({
			variables: {
				offerId,
				title: values.title || null,
				imageId:
					values.image === undefined
						? currentImageFileId ?? null
						: values.image.id || null,
				visibility: values.visibility
					? { kind: values.visibility, dateTime: values.dateTime ?? undefined }
					: undefined,
				// The form always carries the current selection, so send it explicitly: null clears
				// the slot, which blanks the offer owner's product rather than inheriting it.
				licenseProductId: values.licenseProduct?.id ?? null,
				courseContentProductId: values.courseContentProduct?.id ?? null,
				certificationProductId: values.certificationProduct?.id ?? null,
			},
			onCompleted: () => {
				onHide();
				toast.current?.show({
					severity: "success",
					life: 3000,
					summary: t("offerSubscriptions.config.saveSuccess"),
					detail: t("offerSubscriptions.config.saveSuccessDetail"),
				});
				onUpdated();
			},
			onError: () => {
				toast.current?.show({
					severity: "error",
					life: 3000,
					summary: t("offerSubscriptions.config.saveError"),
					detail: t("offerSubscriptions.config.saveErrorDetail"),
				});
			},
		});
	};

	return (
		<>
			<Toast ref={toast} />
			<FormDialogButton<EditSubscriberConfigFormState>
				buttonIcon={EpicIcons.PENCIL}
				dialogTitle={t("offerSubscriptions.config.dialogTitle")}
				submitLabel={t("common:actions.save")}
				cancelLabel={t("common:actions.cancel")}
				disabled={isEditing}
			>
				{({ formikRef, onHide }) => (
					<EditSubscriberConfigForm
						ref={formikRef}
						initialTitle={currentTitle}
						initialImageUrl={currentImageUrl}
						initialVisibility={currentVisibility}
						initialDateTime={currentDateTime}
						initialLicenseProduct={currentLicenseProduct}
						initialCourseContentProduct={currentCourseContentProduct}
						initialCertificationProduct={currentCertificationProduct}
						onSubmit={(values) => handleSubmit(onHide, values)}
					/>
				)}
			</FormDialogButton>
		</>
	);
};
